import React, { Component, Fragment } from 'react'
import ReactMarkdown from 'react-markdown'
import track from 'react-tracking'
import Parser from 'html-react-parser'
import PropTypes from 'prop-types'

import { CoremediaRenderer } from '@ycos/coremedia-renderer'
import BEMHelper from '@ynap/bem-helper'
import { Button, AsyncButton } from '@ynap/button'
import Checkbox from '@ynap/checkbox'
import EmptyList from '@ynap/empty-list'
import { withOptimizely } from '@ynap/optimizely-utils'
import Skeleton from '@ynap/skeleton'
import { WithReducedSiteFurniture } from '@ynap/with-reduced-site-furniture'

import { CountryLanguageSection } from './CountryLanguageSection'
import { filterContent } from './filterUtils'
import { MyAccountNavWrapper } from '..'
import { myaccountPageData, trackUpdatePreferredCountryAndLanguage } from '../analytics'

const bem = new BEMHelper('MarketingPreferences')

export class ConsentHeader extends Component {
    static layoutVariant = 'consent-title'

    static propTypes = {
        item: PropTypes.objectOf({
            teaserTitle: PropTypes.string,
            teaserText: PropTypes.string,
        }),
    }

    render() {
        // after TE is fully released we can either remove this header,
        // or use CM content instead of messages in our bundles
        const { item, hasMigratedToNewTierExpansion } = this.props
        const { teaserTitle, teaserText } = item
        return (
            <div className={bem('header', hasMigratedToNewTierExpansion && 'isTierExpansion')}>
                <h2 className={bem('header-title')}>{teaserTitle}</h2>
                {Parser(teaserText)}
            </div>
        )
    }
}

export class ConsentCheckbox extends Component {
    static layoutVariant = 'consent-checkbox'

    static propTypes = {
        item: PropTypes.objectOf({
            ID: PropTypes.string,
        }),
        form: PropTypes.array,
        handleCheck: PropTypes.func,
    }

    render() {
        const { item, form } = this.props
        const { ID: id } = item

        const checked = form[id] === true

        return (
            <Checkbox
                className={bem('consent-item-checkbox')}
                type="checkbox"
                checked={checked}
                onChange={() => this.props.handleCheck(id, !checked)}
            />
        )
    }
}

export class ConsentPreferences extends Component {
    static layoutVariant = 'consent-preferences'

    static propTypes = {
        children: PropTypes.any,
    }

    render() {
        return this.props.children
    }
}

export class ConsentPreference extends Component {
    static layoutVariant = 'consent-preference'

    static propTypes = {
        item: PropTypes.objectOf({
            teaserTitle: PropTypes.string,
        }),
        children: PropTypes.any,
        hidden: PropTypes.bool,
    }

    render() {
        const { item, children, hidden } = this.props

        return hidden ? null : (
            <div className={bem('consent-item')}>
                {item.teaserTitle ? (
                    <div className={bem('consent-item-title-line')}>
                        <h4 className={bem('consent-item-title')}>{item.teaserTitle}</h4>
                    </div>
                ) : null}
                {children ? children : null}
            </div>
        )
    }
}

export class ConsentItem extends Component {
    static layoutVariant = 'consent-item'

    static propTypes = {
        item: PropTypes.objectOf({
            teaserTitle: PropTypes.string,
            teaserText: PropTypes.string,
        }),
        children: PropTypes.any,
    }

    render() {
        const { item, children } = this.props
        const { teaserTitle, teaserText } = item
        return (
            <div className={bem('consent-item-detail')}>
                {teaserTitle && teaserTitle.length ? (
                    <h4 className={bem('consent-item-detail-title')}>{teaserTitle}</h4>
                ) : null}
                {teaserText ? Parser(teaserText) : null}
                {children ? children : null}
            </div>
        )
    }
}

export class MarketingPreferences extends Component {
    static layoutVariant = 'consents-email-preferences-collection'

    static propTypes = {
        children: PropTypes.any,
    }

    render() {
        const { children } = this.props
        return children
    }
}

const components = [
    MarketingPreferences,
    ConsentHeader,
    ConsentPreferences,
    ConsentPreference,
    ConsentCheckbox,
    ConsentItem,
]

@track()
export class MarketingPreferencesForm extends Component {
    static propTypes = {
        subscriptions: PropTypes.objectOf({
            values: PropTypes.array,
        }),
        preferredCountry: PropTypes.string,
        preferredLanguage: PropTypes.string,
        loyaltyConsent: PropTypes.bool,
        content: PropTypes.object,
        countries: PropTypes.object,
        isStaff: PropTypes.bool,
        messages: PropTypes.objectOf({
            marketing: PropTypes.objectOf({
                cta: PropTypes.func,
            }),
        }),
        locale: PropTypes.object,
        updateUserMarketingCountryLang: PropTypes.func.isRequired,
        isFetchingUser: PropTypes.bool,
        updateSubscriptions: PropTypes.func.isRequired,
        isUpdatingSubscriptions: PropTypes.bool,
        updateSubscriptionsError: PropTypes.bool,
        hasMigratedToNewTierExpansion: PropTypes.bool,
        hideNav: PropTypes.bool,
        trackOptimizely: PropTypes.func,
    }

    constructor(props) {
        super(props)

        this.state = {
            content: undefined,
            marketingPreferencesValues: {},
            rewardsPreferencesValues: {},
            shouldDisplayCountryLangSection: false,
            selectedCountry: null,
            selectedLanguage: null,
        }

        this.handleCheck = this.handleCheck.bind(this)
        this.handleSubmit = this.handleSubmit.bind(this)
    }

    componentDidMount() {
        const { subscriptions, loyaltyConsent, preferredLanguage, preferredCountry } = this.props

        this.setState({
            marketingPreferencesValues: { ...subscriptions.values },
            ...(loyaltyConsent && { rewardsPreferencesValues: loyaltyConsent }),
        })

        if (preferredLanguage && preferredCountry) {
            this.setState({
                selectedLanguage: preferredLanguage,
                selectedCountry: preferredCountry,
            })
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { content, countries, preferredLanguage, preferredCountry, accessKey, email } = this.props
        const { marketingPreferencesValues, selectedCountry, selectedLanguage } = this.state
        const isFromEmail = accessKey && email

        const countryLanguages = countries?.[selectedCountry]?.languages || {}
        const selectedCountryPreferredLanguage = countries?.[selectedCountry]?.preferredLanguage
        const languageKeys = Object.keys(countryLanguages).map((lang) => countryLanguages[lang].locale)

        if (content && content !== prevProps.content) {
            this.setState({ content })
        }

        if (preferredLanguage && preferredLanguage !== prevProps.preferredLanguage) {
            this.setState({ selectedLanguage: preferredLanguage })
        }

        if (preferredCountry && preferredCountry !== prevProps.preferredCountry) {
            this.setState({ selectedCountry: preferredCountry })
        }

        if (marketingPreferencesValues !== prevState.marketingPreferencesValues) {
            const hasMarketingPreferencesChecked = Object.values(marketingPreferencesValues).includes(true)
            this.setState({ shouldDisplayCountryLangSection: !isFromEmail && hasMarketingPreferencesChecked })
        }

        if (selectedCountry !== prevState.selectedCountry) {
            this.setState({
                selectedLanguage:
                    (languageKeys.includes(selectedLanguage) && selectedLanguage) ||
                    countryLanguages[selectedCountryPreferredLanguage]?.locale ||
                    languageKeys[0],
            })
        }
    }

    handleCheck(name, value) {
        if (name === 'maLoyaltyConsent') {
            this.setState({
                rewardsPreferencesValues: {
                    [name]: value,
                },
            })
        } else {
            this.setState({
                marketingPreferencesValues: {
                    ...this.state.marketingPreferencesValues,
                    [name]: value,
                },
            })
        }
    }

    async handleSubmit() {
        const { accessKey, email, updateUserMarketingCountryLang, preferredCountry, preferredLanguage, tracking } =
            this.props

        const { marketingPreferencesValues, selectedCountry, selectedLanguage, shouldDisplayCountryLangSection } =
            this.state

        const isFromEmail = accessKey && email
        if (isFromEmail) {
            this.props.updateSubscriptions(marketingPreferencesValues, { accessKey, email })
        } else {
            const userUpdatePayload = {
                ...(shouldDisplayCountryLangSection &&
                    selectedLanguage && {
                        preferredLanguage: selectedLanguage,
                    }),
                ...buildContextAttribute(this.state),
            }

            await updateUserMarketingCountryLang(userUpdatePayload)
        }

        if (selectedCountry !== preferredCountry || selectedLanguage !== preferredLanguage) {
            let eventParameter = ''

            if (selectedCountry !== preferredCountry) {
                eventParameter = 'country'
            }

            if (selectedLanguage !== preferredLanguage) {
                eventParameter += eventParameter ? '_language' : 'language'
            }

            tracking?.trackEvent(trackUpdatePreferredCountryAndLanguage(eventParameter))
        }

        this.props.trackOptimizely(marketingPreferencesValues)

        if (
            marketingPreferencesValues.maMarketingEmailStandard &&
            marketingPreferencesValues.maMarketingEmailWhatsNew
        ) {
            this.props.trackOptimizely({ optin_account: 'optin_account_preference_center' })
            this.props.trackOptimizely({ optin_account: 'global_opt-in' })
        }
    }

    countryLanguagesOptions = () => {
        const { countries, hasMigratedToNewTierExpansion, messages } = this.props
        const { selectedCountry, selectedLanguage } = this.state

        if (selectedCountry === null) {
            return null
        }

        const onCountryChange = (countryValue) => {
            this.setState({ selectedCountry: countryValue })
        }
        const onLanguageChange = (languageValue) => {
            this.setState({ selectedLanguage: languageValue })
        }

        return (
            <CountryLanguageSection
                countries={countries}
                hasMigratedToNewTierExpansion={hasMigratedToNewTierExpansion}
                messages={messages}
                selectedCountry={selectedCountry}
                selectedLanguage={selectedLanguage}
                onCountryChange={onCountryChange}
                onLanguageChange={onLanguageChange}
            />
        )
    }

    render() {
        const {
            content,
            messages,
            locale,
            isUpdatingSubscriptions,
            updateSubscriptionsError,
            updateUserMarketingCountryLangError,
            subscriptions,
            hasMigratedToNewTierExpansion,
            isStaff,
            hideNav,
            nativeApp,
        } = this.props

        const asyncState = {
            error: !!updateSubscriptionsError || updateUserMarketingCountryLangError,
            loading: !!isUpdatingSubscriptions || isUpdatingSubscriptions,
            primaryAction: {
                message: messages.marketing.cta({ hasMigratedToNewTierExpansion }),
                click: this.handleSubmit,
            },
        }

        const { marketingCommunicationsItems, rewardsProgramItems } = filterContent(
            content,
            subscriptions,
            hasMigratedToNewTierExpansion
        )

        return (
            <div className={bem(hideNav ? 'guestUser' : null, nativeApp || hideNav ? 'nativeApp' : null)}>
                {hasMigratedToNewTierExpansion && (
                    <>
                        <div className={bem('consent-info')}>{messages.marketing.info()}</div>
                        <h1 className={bem('consent-section-title')}>
                            {messages.marketing.marketingCommunicationsSectionTitle()}
                        </h1>
                    </>
                )}

                {/* Marketing Communications Preferences */}
                {CoremediaRenderer({ items: marketingCommunicationsItems }, components, {
                    rules: [
                        ({ item }) => {
                            if (item.layoutVariant == 'consent-checkbox') {
                                return {
                                    item,
                                    form: this.state.marketingPreferencesValues,
                                    handleCheck: this.handleCheck,
                                }
                            }
                            if (item.layoutVariant == 'consent-title') {
                                return {
                                    item: {
                                        ...item,
                                    },
                                    hasMigratedToNewTierExpansion,
                                }
                            }
                        },
                    ],
                })}
                {/* Rewards Program Preferences */}
                {!isStaff && hasMigratedToNewTierExpansion && rewardsProgramItems.length > 0 && (
                    <>
                        <h1 className={bem('consent-section-title', 'withDivider')}>
                            {messages.marketing.rewardsAccountSectionTitle()}
                        </h1>
                        {CoremediaRenderer({ items: rewardsProgramItems }, components, {
                            rules: [
                                ({ item }) => {
                                    if (item.layoutVariant == 'consent-checkbox') {
                                        return {
                                            item: {
                                                ...item,
                                            },
                                            form: this.state.rewardsPreferencesValues,
                                            handleCheck: this.handleCheck,
                                        }
                                    }
                                },
                            ],
                        })}
                        {/* Country and Language Preferences */}
                        {this.state.shouldDisplayCountryLangSection && this.countryLanguagesOptions()}

                        <div
                            className={bem(
                                'consent-more-details',
                                this.state.shouldDisplayCountryLangSection && 'withLanguageOptions'
                            )}
                        >
                            <ReactMarkdown
                                components={{
                                    a: (props) => (
                                        <Button
                                            type="link"
                                            component="a"
                                            target="_blank"
                                            rel="noopener"
                                            className={bem('consent-more-details-link')}
                                            href={props.href}
                                        >
                                            {props.children}
                                        </Button>
                                    ),
                                }}
                                allowedElements={['a']}
                                unwrapDisallowed
                            >
                                {messages.marketing.moreDetails({ locale: locale.id })}
                            </ReactMarkdown>
                        </div>
                    </>
                )}
                {/* Country and Language Preferences */}
                {(!hasMigratedToNewTierExpansion || isStaff) &&
                    this.state.shouldDisplayCountryLangSection &&
                    this.countryLanguagesOptions()}

                <div className={bem('consent-button-container')}>
                    <AsyncButton
                        type="primary"
                        asyncState={asyncState}
                        className={bem('consent-button')}
                        messages={{
                            notifications: {
                                errors: messages.marketing.updateError,
                            },
                        }}
                        notificationPosition="bottom"
                        arrowPosition="top"
                    />
                </div>
            </div>
        )
    }
}

@track()
export class MarketingPreferencesPage extends Component {
    static propTypes = {
        messages: PropTypes.shape({
            marketing: PropTypes.shape({
                updateError: PropTypes.func,
            }),
        }).isRequired,
        subscriptions: PropTypes.object,
        loyaltyConsent: PropTypes.object,
        isFetchingSubscriptions: PropTypes.bool,
        fetchSubscriptionsError: PropTypes.bool,
        fetchContent: PropTypes.func.isRequired,
        fetchCountry: PropTypes.func.isRequired,
        fetchSubscriptions: PropTypes.func.isRequired,
        fetchLoyaltyConsent: PropTypes.func,
        isUpdatingSubscriptions: PropTypes.bool,
        updateSubscriptionsError: PropTypes.bool,
        updateSubscriptions: PropTypes.func.isRequired,
        updateUserInfo: PropTypes.func.isRequired,
        updateUserMarketingCountryLang: PropTypes.func.isRequired,
        isFetchingLoyaltyConsent: PropTypes.bool,
        fetchLoyaltyConsentError: PropTypes.object,
        userSummary: PropTypes.shape({
            hasMigratedToNewTierExpansion: PropTypes.bool,
            isEip: PropTypes.bool,
        }),
        preferredCountry: PropTypes.string,
        preferredLanguage: PropTypes.string,
        isStaff: PropTypes.bool,
        country: PropTypes.array,
        countries: PropTypes.object,
        content: PropTypes.object,
        location: PropTypes.string,
        locale: PropTypes.shape({
            id: PropTypes.string,
        }),
        isFetchingCountry: PropTypes.bool,
        isFetchingContent: PropTypes.bool,
        fetchCountryError: PropTypes.bool,
        fetchContentError: PropTypes.bool,
        getLinks: PropTypes.func.isRequired,
        optimizely: PropTypes.object,
        isCoremediaV2: PropTypes.bool,
    }

    @track(
        myaccountPageData({
            name: 'email preferences',
            subCategory1: 'email preferences',
        })
    )
    getContentKey = (hasMigratedToNewTierExpansion, isEip) =>
        hasMigratedToNewTierExpansion
            ? isEip
                ? 'consents-loyalty-eips-email-preferences-collection'
                : 'consents-loyalty-email-preferences-collection'
            : 'consents-email-preferences-collection'

    componentDidMount() {
        const {
            country,
            content,
            fetchSubscriptions,
            fetchUser,
            fetchContent,
            fetchCountry,
            fetchCountries,
            isFetchingCountry,
            accessKey,
            email,
            userSummary,
            isCoremediaV2,
        } = this.props

        const { hasMigratedToNewTierExpansion = false, isEip } = userSummary || {}
        const isFromEmail = accessKey && email

        if (!isFromEmail) {
            fetchUser()
            fetchCountries()
        }

        fetchSubscriptions({ accessKey, email })

        if (typeof content === 'undefined') {
            if (!country && !isFetchingCountry) {
                fetchCountry().then(() => {
                    if (typeof content === 'undefined' || typeof content?.response === 'undefined') {
                        fetchContent(
                            this.getContentKey(hasMigratedToNewTierExpansion, isEip),
                            null,
                            null,
                            isCoremediaV2
                        )
                    }
                })
            } else if ((country && typeof content === 'undefined') || typeof content?.response === 'undefined') {
                fetchContent(this.getContentKey(hasMigratedToNewTierExpansion, isEip), null, null, isCoremediaV2)
            }
        }
    }

    componentDidUpdate(prevProps) {
        const { content, fetchContent, userSummary, isCoremediaV2 } = this.props
        const { hasMigratedToNewTierExpansion = false, isEip } = userSummary || {}

        if (userSummary && userSummary !== prevProps.userSummary) {
            if (typeof content === 'undefined' || typeof content?.response === 'undefined') {
                fetchContent(this.getContentKey(hasMigratedToNewTierExpansion, isEip), null, null, isCoremediaV2)
            }
        }
    }

    render() {
        const {
            content,
            countries,
            subscriptions,
            loyaltyConsent,
            preferredLanguage,
            preferredCountry,
            messages,
            locale,
            location,
            fetchContent,
            fetchCountry,
            isFetchingCountry,
            isFetchingContent,
            isFetchingSubscriptions,
            fetchCountryError,
            fetchContentError,
            fetchSubscriptions,
            fetchSubscriptionsError,
            updateSubscriptions,
            isUpdatingSubscriptions,
            updateSubscriptionsError,
            updateUserInfo,
            updateUserMarketingCountryLang,
            updateUserMarketingCountryLangError,
            isUpdatingUserMarketingCountryLang,
            userSummary,
            isFetchingUser,
            fetchUserError,
            getLinks,
            nativeApp,
            config,
            accessKey,
            email,
            isStaff,
            optimizely,
            isCoremediaV2,
        } = this.props

        const { fullPageHeader } = config?.head?.flags || {}

        const { hasMigratedToNewTierExpansion = false, isEip } = userSummary || {}

        // @TODO Move getLinks to state - https://jira.nap/browse/YMA-1052
        const accountNavProps = {
            userSummary,
            messages,
            location,
            getLinks,
            fullPageHeader,
            title: messages?.pages?.marketingPreferences?.title({ hasMigratedToNewTierExpansion }),
        }
        const loading = isFetchingCountry || isFetchingContent || isFetchingSubscriptions
        const error = !!fetchCountryError || !!fetchContentError || !!fetchSubscriptionsError || !!fetchUserError
        const isFromEmail = accessKey && email
        const hideNav = !!isFromEmail

        const trackOptimizely = (values) => {
            Object.entries(values).forEach(([name, value]) => {
                switch (name) {
                    case 'maMarketingEmailDaily':
                        optimizely?.track(
                            value
                                ? 'emailpreferences_mrktemaildailychecked'
                                : 'emailpreferences_mrktemaildailyunchecked'
                        )
                        break

                    case 'maMarketingEmailStandard':
                        optimizely?.track(
                            value ? 'emailpreferences_mrktemailstdchecked' : 'emailpreferences_mrktemailstdunchecked'
                        )
                        break
                    case 'maMarketingEmailWhatsNew':
                        optimizely?.track(
                            value
                                ? 'emailpreferences_mrktemailwhatsnewchecked'
                                : 'emailpreferences_mrktemailwhatsnewunchecked'
                        )
                        break
                    case 'optin_account':
                        optimizely?.track(value)
                        break
                    default:
                        break
                }
            })
            optimizely?.track('emailpreferences_updtemailprefclicks')
        }

        const wcsComponent =
            content && subscriptions ? (
                <MarketingPreferencesForm
                    content={content}
                    countries={countries}
                    subscriptions={subscriptions}
                    loyaltyConsent={loyaltyConsent}
                    preferredLanguage={preferredLanguage}
                    preferredCountry={preferredCountry}
                    updateSubscriptions={updateSubscriptions}
                    isUpdatingSubscriptions={isUpdatingSubscriptions}
                    updateSubscriptionsError={updateSubscriptionsError}
                    updateUserMarketingCountryLangError={updateUserMarketingCountryLangError}
                    isUpdatingUserMarketingCountryLang={isUpdatingUserMarketingCountryLang}
                    updateUserInfo={updateUserInfo}
                    updateUserMarketingCountryLang={updateUserMarketingCountryLang}
                    isFetchingUser={isFetchingUser}
                    hasMigratedToNewTierExpansion={hasMigratedToNewTierExpansion}
                    messages={messages}
                    locale={locale}
                    accessKey={accessKey}
                    email={email}
                    isStaff={isStaff}
                    nativeApp={nativeApp}
                    hideNav={hideNav}
                    trackOptimizely={trackOptimizely}
                />
            ) : error ? (
                <EmptyList
                    className={bem('empty')}
                    icon={<div className={bem('emptyStateIcon')} />}
                    variant={!isFromEmail ? 'myAccountError' : ''}
                    title={messages.marketingPreferences.pageError()}
                >
                    <Button
                        onClick={() => {
                            if (fetchCountryError) {
                                fetchCountry().then(() => {
                                    fetchContent(
                                        this.getContentKey(hasMigratedToNewTierExpansion, isEip),
                                        null,
                                        null,
                                        isCoremediaV2
                                    )
                                })
                            }

                            if (fetchContentError) {
                                fetchContent(
                                    this.getContentKey(hasMigratedToNewTierExpansion, isEip),
                                    null,
                                    null,
                                    isCoremediaV2
                                )
                            }

                            if (fetchSubscriptionsError) {
                                fetchSubscriptions({ accessKey, email })
                            }
                        }}
                        type="primary-link"
                        className={bem('emptyLink')}
                    >
                        {messages.marketingPreferences.retryNow()}
                    </Button>
                </EmptyList>
            ) : null

        const skeleton = (
            <Fragment>
                <div className={bem('titleSkeleton')}>
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                </div>
                <div className={bem('skeletonTwo')}>
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                    <Skeleton align={'left'} />
                </div>
                <div className={bem('defaultSkeleton')}>
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                </div>
                <div className={bem('defaultSkeleton')}>
                    <Skeleton count={2} align={'left'} className={bem('skeletonSpace')} />
                </div>
                <div className={bem('longParagraph', isFromEmail ? 'center' : '')}>
                    <Skeleton count={2} className={bem('skeletonSpace')} />
                    <Skeleton align={'left'} />
                </div>
                <div className={bem('longParagraph', `last${isFromEmail ? '-center' : ''}`)}>
                    <Skeleton count={2} className={bem('skeletonSpace')} />
                    <Skeleton align={'left'} />
                </div>
                <Skeleton
                    count={1}
                    containerClassName={isFromEmail ? bem('skeletonButtonWrapper') : ''}
                    className={bem('skeletonButton')}
                />
            </Fragment>
        )

        return nativeApp || hideNav ? (
            <WithReducedSiteFurniture>{loading && !error ? skeleton : wcsComponent}</WithReducedSiteFurniture>
        ) : (
            <MyAccountNavWrapper {...accountNavProps}>
                {loading && !error ? skeleton : wcsComponent}
            </MyAccountNavWrapper>
        )
    }
}

export function buildContextAttribute({
    marketingPreferencesValues,
    rewardsPreferencesValues,
    selectedCountry,
    shouldDisplayCountryLangSection,
}) {
    const contextAttribute = []
    let hasOptedIn = false

    for (const value in marketingPreferencesValues) {
        const prefValue = marketingPreferencesValues[value].toString()
        if (prefValue === 'true') hasOptedIn = true

        contextAttribute.push({
            attributeName: value,
            attributeValue: {
                value: prefValue,
            },
        })
    }

    if (shouldDisplayCountryLangSection && selectedCountry) {
        contextAttribute.push({
            attributeName: 'maPreferredCountry',
            attributeValue: {
                value: selectedCountry,
            },
        })
    }

    if (Object.hasOwn(rewardsPreferencesValues, 'maLoyaltyConsent')) {
        contextAttribute.push({
            attributeName: 'maLoyaltyConsent',
            attributeValue: {
                value: rewardsPreferencesValues.maLoyaltyConsent.toString(),
            },
        })
    }

    contextAttribute.push({
        attributeName: 'maMarketingConsent',
        attributeValue: {
            value: hasOptedIn.toString(),
        },
    })

    return { contextAttribute }
}

export default withOptimizely(MarketingPreferencesPage)
